/* eslint-disable @typescript-eslint/consistent-type-assertions */

import {
  type ActorRefFrom,
  and,
  enqueueActions,
  type MachineImplementationsFrom,
  not,
  setup,
} from 'xstate5';

import { createPlaceholderAction } from './helpers';
import { Actions, Actors, Guards, Inputs } from './setup';
import { type Context, type Event, type Input } from './types';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  guards: {
    //
    // ─── External Guards ─────────────────────────────────────────────────

    checkIfShouldStartCustomization: () => true,
    checkIfShouldDefaultToMixedDressing: () => false,

    // ─── Internal Guards ─────────────────────────────────────────────────

    checkIfCustomProduct: Guards.checkIfCustomProduct,
    checkIfExceedsMaxModifications: Guards.checkIfExceedsMaxModifications,
    checkIfExceedsMaxModificationsAfterAddition: Guards.checkIfExceedsMaxModificationsAfterAddition, // prettier-ignore
    checkIfExceedsMaxModificationsAfterRemoval: Guards.checkIfExceedsMaxModificationsAfterRemoval, // prettier-ignore
    checkIfRequiredModification: Guards.checkIfRequiredModification,
    checkIfExceedsAllowedMultipleQuantities: Guards.checkIfExceedsAllowedMultipleQuantities, // prettier-ignore
    checkIfExceedsMaxAggregateQuantity: Guards.checkIfExceedsMaxAggregateQuantity, // prettier-ignore
    checkIfHasOutOfStockModifications: Guards.checkIfHasOutOfStockModifications,
    checkIfInStockIngredient: Guards.checkIfInStockIngredient,
    checkIfTooFewBasesSelected: Guards.checkIfTooFewBasesSelected,
    checkIfDressingsCanBeMixed: Guards.checkIfDressingsCanBeMixed,
    checkIfNoDressingSelected: Guards.checkIfNoDressingSelected,
    checkIfHasUnmetMinLimitGroup: Guards.checkIfHasUnmetMinLimitGroup,
    checkIfHasMixedDressingDetails: Guards.checkIfHasMixedDressingDetails,
    checkIfDressingDetailsEnabled: Guards.checkIfDressingDetailsEnabled,
  },
  actions: {
    //
    // ─── External Actions ────────────────────────────────────────────────

    onOutOfStockIngredientsFound: createPlaceholderAction('onOutOfStockIngredientsFound'), // prettier-ignore
    onMaxModificationsExceeded: createPlaceholderAction('onMaxModificationsExceeded'), // prettier-ignore
    onMaxKindQtyExceeded: createPlaceholderAction('onMaxKindQtyExceeded'),
    onMaxKindExceeded: createPlaceholderAction('onMaxKindExceeded'),
    onMinUnmet: createPlaceholderAction('onMinUnmet'),
    onFailedToAddModification: createPlaceholderAction('onFailedToAddModification'), // prettier-ignore
    onModificationAdded: createPlaceholderAction('onModificationAdded'),
    onModificationRemoved: createPlaceholderAction('onModificationRemoved'),
    onCustomizationEnd: createPlaceholderAction('onCustomizationEnd'),
    onCustomizationCancel: createPlaceholderAction('onCustomizationCancel'),
    onCustomizationComplete: createPlaceholderAction('onCustomizationComplete'),

    // ─── Internal Actions ────────────────────────────────────────────────

    storeCustomizationData: Actions.storeCustomizationData,
    generateModifications: Actions.generateModifications,
    generateModifiers: Actions.generateModifiers,
    storeLastKnownModifications: Actions.storeLastKnownModifications,
    resetLastKnowModifications: Actions.resetLastKnowModifications,
    resetModifications: Actions.cancelPendingChanges,
    addModification: Actions.addModification,
    removeModification: Actions.removeModification,
    removeModificationCompletely: Actions.removeModificationCompletely,
    calculateCalories: Actions.calculateCalories,
    generateMixedDressingDetails: Actions.generateMixedDressingDetails,
    raiseChangeToOnTheSideIfNeeded: Actions.raiseChangeToOnTheSideIfNeeded,
    raiseGenerateMixedDressingDetailsEvent: Actions.raiseGenerateMixedDressingDetailsEvent, // prettier-ignore
    resetMixedDressingDetails: Actions.resetMixedDressingDetails,
    resetDressingPortions: Actions.resetDressingPortions,
    increaseMixedDressingWeight: Actions.increaseMixedDressingWeight,
    decreaseMixedDressingWeight: Actions.decreaseMixedDressingWeight,
    calculateNetPrice: Actions.calculateNetPrice,
    setCustomName: Actions.setCustomName,
    storeRequiredIngredientName: Actions.storeRequiredIngredientName,
    clearRequiredIngredientName: Actions.clearRequiredIngredientName,

    // ─── Conditional and Chained Actions ─────────────────────────────────

    updateModificationAfterChange: Actions.updateModificationAfterChange,
    triggerOnOutOfStockIngredientsFound: enqueueActions((args) => {
      const { enqueue, check } = args;
      const hasOutOfStockIngredients = check(Guards.checkIfHasOutOfStockModifications); // prettier-ignore

      if (!hasOutOfStockIngredients) return;

      enqueue('onOutOfStockIngredientsFound');
    }),
  },
  actors: {
    fetchCustomizationData: Actors.fetchCustomizationData,
  },
});

// ─────────────────────────────────────────────────────────────────────

export const customizationMachine = model.createMachine({
  initial: 'fetching',
  context: ({ input }) => ({
    productSlug: input.productSlug,
    restaurantSlug: input.restaurantSlug,
    ingredientAssetVersion: input.ingredientAssetVersion,
    lineItemId: input.lineItemId,
    isDelivery: input.isDelivery,
    defaultDressingWeight: input.defaultDressingWeight,

    product: undefined,
    productLineItem: undefined,
    productRestaurant: undefined,
    quantity: 1,

    modificationLimits: {},
    allModificationsMap: undefined,
    modifications: undefined,
    modificationGroups: undefined,
    mixedDressingDetails: [],
    calories: 0,
    netPrice: 0,
    lastKnownModifications: undefined,
    lastKnownMixedDressingDetails: [],
    customName: undefined,
    requiredIngredientName: undefined,
  }),
  states: {
    fetching: {
      invoke: {
        src: 'fetchCustomizationData',
        input: Inputs.getFetchCustomizationDataInput,
        onDone: {
          target: 'undetermined',
          actions: [
            { type: 'storeCustomizationData', params: (args) => args.event.output }, // prettier-ignore
          ],
        },
        onError: {
          target: 'failed-to-fetch',
        },
      },
    },
    'failed-to-fetch': {},
    undetermined: {
      always: [
        {
          target: 'ready.mode.customization',
          guard: 'checkIfShouldStartCustomization',
        },
        {
          target: 'ready.mode.customization',
          guard: 'checkIfCustomProduct',
        },
        {
          target: 'ready.mode.customization',
        },
      ],
    },
    ready: {
      entry: [
        'generateModifications',
        'updateModificationAfterChange',
        'triggerOnOutOfStockIngredientsFound',
      ],
      type: 'parallel',
      states: {
        mode: {
          initial: 'customization',
          states: {
            customization: {
              entry: ['storeLastKnownModifications'],
              exit: ['calculateCalories'],
              initial: 'active',
              states: {
                active: {},
                'confirm-too-few-bases': {
                  on: {
                    'customization.continue': 'active',
                    'customization.complete': [
                      {
                        target: 'confirm-no-dressing',
                        guard: 'checkIfNoDressingSelected',
                      },
                      {
                        target: '#customization-details',
                        guard: 'checkIfDressingDetailsEnabled',
                      },
                      {
                        target: 'active',
                        actions: ['onCustomizationComplete'],
                      },
                    ],
                  },
                },
                'confirm-no-dressing': {
                  on: {
                    'customization.continue': 'active',
                    'customization.complete': [
                      {
                        target: '#customization-details',
                        guard: 'checkIfDressingDetailsEnabled',
                      },
                      {
                        target: 'active',
                        actions: ['onCustomizationComplete'],
                      },
                    ],
                  },
                },
                'required-modification': {
                  on: {
                    'customization.continue': 'active',
                  },
                  exit: ['clearRequiredIngredientName'],
                },
              },
              on: {
                'customization.complete': [
                  {
                    actions: ['onMaxModificationsExceeded'],
                    guard: 'checkIfExceedsMaxModifications',
                  },
                  {
                    actions: ['onMinUnmet'],
                    guard: 'checkIfHasUnmetMinLimitGroup',
                  },
                  {
                    target: '.confirm-too-few-bases',
                    guard: 'checkIfTooFewBasesSelected',
                  },
                  {
                    target: '.confirm-no-dressing',
                    guard: 'checkIfNoDressingSelected',
                  },
                  {
                    target: '#customization-details',
                    actions: ['onCustomizationEnd'],
                    guard: 'checkIfDressingDetailsEnabled',
                  },
                  {
                    target: 'customization',
                    actions: ['onCustomizationComplete'],
                  },
                ],
                'customization.cancel': {
                  target: '#customization-details',
                  actions: [
                    'resetModifications',
                    'updateModificationAfterChange',
                    'onCustomizationCancel',
                  ],
                },
                'customization.add-modification': [
                  {
                    guard: 'checkIfExceedsMaxModificationsAfterAddition',
                    actions: ['onMaxModificationsExceeded'],
                  },
                  {
                    guard: 'checkIfExceedsAllowedMultipleQuantities',
                    actions: ['onMaxKindQtyExceeded'],
                  },
                  {
                    guard: 'checkIfExceedsMaxAggregateQuantity',
                    actions: ['onMaxKindExceeded'],
                  },
                  {
                    guard: 'checkIfInStockIngredient',
                    actions: [
                      'addModification',
                      'updateModificationAfterChange',
                      'onModificationAdded',
                    ],
                  },
                  {
                    actions: ['onFailedToAddModification'],
                  },
                ],
                'customization.remove-modification': [
                  {
                    guard: 'checkIfRequiredModification',
                    target: '.required-modification',
                    actions: ['storeRequiredIngredientName'],
                  },
                  {
                    guard: 'checkIfExceedsMaxModificationsAfterRemoval',
                    actions: ['onMaxModificationsExceeded'],
                  },
                  {
                    actions: [
                      'removeModification',
                      'updateModificationAfterChange',
                      'onModificationRemoved',
                    ],
                  },
                ],
                'customization.remove-modification-completely': [
                  {
                    guard: 'checkIfRequiredModification',
                    target: '.required-modification',
                    actions: ['storeRequiredIngredientName'],
                  },
                  {
                    actions: [
                      'removeModificationCompletely',
                      'updateModificationAfterChange',
                      'onModificationRemoved',
                    ],
                  },
                ],
              },
            },
            details: {
              id: 'customization-details',
              entry: ['resetLastKnowModifications'],
              on: {
                'customization.start': {
                  target: 'customization',
                },
                'customization.complete': {
                  actions: ['onCustomizationComplete'],
                },
              },
            },
          },
        },
        'dressing-mode': {
          initial: 'undetermined',
          states: {
            undetermined: {
              always: [
                {
                  target: 'mixed-in',
                  guard: and(['checkIfDressingsCanBeMixed', 'checkIfShouldDefaultToMixedDressing']), // prettier-ignore
                },
                {
                  target: 'mixed-in',
                  guard: and(['checkIfDressingsCanBeMixed', 'checkIfHasMixedDressingDetails']), // prettier-ignore
                },
                {
                  target: 'on-the-side',
                },
              ],
            },
            'on-the-side': {
              entry: [
                'resetMixedDressingDetails',
                'updateModificationAfterChange',
              ],
              on: {
                'customization.dressing-mode.mixed-in': {
                  target: 'mixed-in',
                  guard: 'checkIfDressingsCanBeMixed',
                },
                'customization.dressing-mode.toggle': {
                  target: 'mixed-in',
                  guard: 'checkIfDressingsCanBeMixed',
                },
              },
            },
            'mixed-in': {
              entry: ['resetDressingPortions', 'updateModificationAfterChange'],
              on: {
                'customization.dressing-mode.change-to-on-the-side-if-needed': {
                  description:
                    'Change the dressing mode to "on the side" if the active ingredients are not mixable anymore.',
                  target: 'on-the-side',
                  guard: not('checkIfDressingsCanBeMixed'),
                },
                'customization.dressing-mode.on-the-side': {
                  target: 'on-the-side',
                },
                'customization.dressing-mode.toggle': {
                  target: 'on-the-side',
                },
                'customization.dressing-mode.generate-mixed-dressings-details':
                  {
                    actions: [
                      'generateMixedDressingDetails',
                      'calculateCalories',
                    ],
                  },
              },
            },
          },
          on: {
            'customization.dressing.change-portions': {
              actions: Actions.changeDressingPortions,
            },
            'customization.dressing.change-weight': {
              actions: Actions.changeDressingWeight,
            },
            'customization.dressing.increase-portions': [
              {
                guard: 'checkIfExceedsMaxModificationsAfterAddition',
                actions: ['onMaxModificationsExceeded'],
              },
              {
                guard: 'checkIfExceedsMaxAggregateQuantity',
                actions: ['onMaxKindExceeded'],
              },
              {
                guard: 'checkIfInStockIngredient',
                actions: [
                  'addModification',
                  'updateModificationAfterChange',
                  'onModificationAdded',
                ],
              },
              {
                actions: ['onFailedToAddModification'],
              },
            ],
            'customization.dressing.decrease-portions': [
              {
                guard: 'checkIfExceedsMaxModificationsAfterRemoval',
                actions: ['onMaxModificationsExceeded'],
              },
              {
                actions: [
                  'removeModification',
                  'updateModificationAfterChange',
                  'onModificationRemoved',
                ],
              },
            ],
            'customization.dressing.increase-weight': [
              { actions: ['increaseMixedDressingWeight', 'calculateCalories'] },
            ],
            'customization.dressing.decrease-weight': [
              { actions: ['decreaseMixedDressingWeight', 'calculateCalories'] },
            ],
          },
        },
      },
      on: {
        'customization.set-custom-name': {
          actions: 'setCustomName',
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type CustomizationMachineActorRef = ActorRefFrom<typeof customizationMachine>; // prettier-ignore
export type CustomizationMachineImplementations = MachineImplementationsFrom<typeof customizationMachine>; // prettier-ignore
